import { createStore, type StoreApi } from "zustand/vanilla";
import { expiredTrashOperations, invertOperations, OperationFactory, resolveRunningConflicts, type Dataset, type SyncOperation } from "@otrack/domain";
import type { LocalStore } from "@otrack/local-store";

/** `undoExtra` reverts what the step changed outside the Dataset (Google Calendar). */
interface HistoryStep { before: Dataset; operations: SyncOperation[]; undoExtra?: () => void }

export interface AppState {
  ready: boolean;
  dataset: Dataset;
  /** Applies operations. Undoable ones can be reverted with `undo`, along with `undoExtra`. */
  dispatch: (operations: SyncOperation[], options?: { undoable?: boolean; undoExtra?: () => void }) => Promise<void>;
  undo: () => Promise<boolean>;
  canUndo: () => boolean;
  /** Called by sync after operations from other devices were applied locally. */
  receiveRemote: (operations: SyncOperation[]) => Promise<void>;
  /** Notified after every local change, so sync can push it. */
  onLocalChange: (listener: () => void) => () => void;
  factory: OperationFactory;
}

export type AppStore = StoreApi<AppState>;

export async function createAppStore(local: LocalStore, now: () => number = Date.now): Promise<AppStore> {
  await local.initialize();
  const factory = new OperationFactory(await local.getDeviceId(), now);
  let initial = await local.load();
  // Trash keeps items for 30 days; anything older is removed for good on startup.
  const expired = expiredTrashOperations(initial, factory, now());
  if (expired.length) initial = await local.apply(expired);
  const history: HistoryStep[] = [];
  const localListeners = new Set<() => void>();
  const changed = () => { for (const listener of localListeners) listener(); };

  const store = createStore<AppState>()((set, get) => ({
    ready: true,
    dataset: initial,
    factory,
    async dispatch(operations, options = {}) {
      if (!operations.length) {
        if (options.undoExtra && options.undoable !== false) history.push({ before: get().dataset, operations, undoExtra: options.undoExtra });
        return;
      }
      const before = get().dataset;
      let dataset = await local.apply(operations);
      // A Timer may also be running on another device; the latest start always wins.
      const conflicts = resolveRunningConflicts(dataset, factory);
      if (conflicts.length) dataset = await local.apply(conflicts);
      if (options.undoable !== false) {
        history.push({ before, operations, ...(options.undoExtra ? { undoExtra: options.undoExtra } : {}) });
        if (history.length > 50) history.shift();
      }
      set({ dataset });
      changed();
    },
    async undo() {
      const step = history.pop();
      if (!step) return false;
      step.undoExtra?.();
      if (!step.operations.length) return true;
      const inverse = invertOperations(step.before, step.operations, factory, now());
      set({ dataset: await local.apply(inverse) });
      changed();
      return true;
    },
    canUndo: () => history.length > 0,
    async receiveRemote(operations) {
      for (const operation of operations) factory.observe(operation.clock);
      const dataset = await local.load();
      const conflicts = resolveRunningConflicts(dataset, factory);
      set({ dataset: conflicts.length ? await local.apply(conflicts) : dataset });
      if (conflicts.length) changed();
    },
    onLocalChange(listener) {
      localListeners.add(listener);
      return () => localListeners.delete(listener);
    }
  }));

  local.subscribe((dataset) => store.setState({ dataset }));
  return store;
}
